import { type ReactNode } from "react"

type BrandTier = "popular" | "luxury" | "value" | "budget"

interface BrandListCardProps {
  /** Brand logo (image element or fallback initial) */
  logo?: ReactNode
  /** Brand name */
  name: string
  /** Brand tier for badge styling */
  tier?: BrandTier
  /** Number of products */
  productCount?: number
  /** Price range string (e.g. "$299 – $3,499") */
  priceRange?: string
  /** Product type labels */
  productTypes?: string[]
  /** Link destination */
  href?: string
  /** Additional classes on the wrapper */
  className?: string
}

const TIER_CLASSES: Record<BrandTier, string> = {
  popular: "bg-[#ff9951] text-[var(--color-brand-action-text)]",
  luxury: "bg-[#0469f0] text-white",
  value: "bg-gray-200 text-gray-600",
  budget: "bg-gray-100 text-gray-500",
}

const TIER_LABELS: Record<BrandTier, string> = {
  popular: "Popular",
  luxury: "Luxury",
  value: "Value",
  budget: "Budget",
}

export function BrandListCard({
  logo,
  name,
  tier,
  productCount,
  priceRange,
  productTypes,
  href,
  className = "",
}: BrandListCardProps) {
  const Wrapper = href ? "a" : "div"
  const wrapperProps = href ? { href } : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={`group flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 hover:border-[#ff9951]/30 hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] transition-all ${className}`}
    >
      {/* Logo */}
      <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {logo || (
          <span className="text-sm font-bold text-gray-600">
            {name.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + tier badge */}
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#0469f0] transition-colors">
            {name}
          </h3>
          {tier && (
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${TIER_CLASSES[tier]}`}
            >
              {TIER_LABELS[tier]}
            </span>
          )}
        </div>

        {/* Product count + price range */}
        {(productCount != null || priceRange) && (
          <p className="text-[11px] text-gray-600 mb-1">
            {productCount != null && <>{productCount} products</>}
            {productCount != null && priceRange && <> · </>}
            {priceRange}
          </p>
        )}

        {/* Product types */}
        {productTypes && productTypes.length > 0 && (
          <p className="text-[11px] text-gray-600 truncate">
            {productTypes.slice(0, 3).join(", ")}
            {productTypes.length > 3 && ` +${productTypes.length - 3} more`}
          </p>
        )}
      </div>
    </Wrapper>
  )
}
